/**
 * Выравнивание ресурсов (ПЛ-14): сколько людей и техники доступно одновременно, режим — в пределах резерва
 * или со сдвигом окончания. Сначала расчёт «что получится», потом «Применить» — правка в журнале, откатывается.
 */
import type { ScheduleView } from '@epc/contracts';
import { useMutation, useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { Button } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Input } from '../../ui/form';
import { fmtMoment } from './model';

interface Suggest {
  resourceId: string;
  name: string;
  kind: string;
  peak: number;
  onSite: number | null;
}
interface Report {
  finishBefore: string | null;
  finishAfter: string | null;
  moved: Array<{ id: string; code: string; name: string; startBefore: string; startAfter: string }>;
  peaks: Array<{ resourceId: string; name: string; before: number; after: number; limit: number }>;
  unresolved: Array<{ resource: string; day: string; need: number; limit: number }>;
  stoppedEarly: boolean;
  applied: boolean;
  view: ScheduleView | null;
}
const dd = (s: string) => s.slice(0, 10).split('-').reverse().join('.');

export function LevelDialog({
  view,
  onClose,
  onView,
}: {
  view: ScheduleView;
  onClose: () => void;
  onView: (v: ScheduleView) => void;
}) {
  const id = view.schedule.id;
  const q = useQuery({
    queryKey: ['level', id, view.schedule.revision],
    queryFn: () => api<Suggest[]>(`/schedules/${id}/level`),
  });
  const [limits, setLimits] = useState<Record<string, string>>({});
  const [mode, setMode] = useState<'float' | 'extend'>('float');
  const [zones, setZones] = useState<Record<string, string>>({});
  const [rep, setRep] = useState<Report | null>(null);
  // Ввод изменился — прежний расчёт не применяется, нужен новый «Посчитать»
  const edit =
    <T,>(f: (x: T) => void) =>
    (x: T) => {
      f(x);
      setRep(null);
    };
  useEffect(() => {
    if (!q.data) return;
    setLimits((l) => {
      const n = { ...l };
      for (const r of q.data)
        if (n[r.resourceId] === undefined) n[r.resourceId] = r.onSite ? String(r.onSite) : '';
      return n;
    });
  }, [q.data]);
  const chosen = Object.entries(limits)
    .filter(([, v]) => v.trim() !== '' && Number(v.replace(',', '.')) >= 0)
    .map(([resourceId, v]) => ({ resourceId, units: Number(v.replace(',', '.')) }));
  const fronts = Object.entries(zones)
    .filter(([, v]) => /^\d+$/.test(v.trim()) && Number(v) >= 1)
    .map(([zoneId, v]) => ({ zoneId, works: Number(v) }));
  const run = useMutation({
    mutationFn: (apply: boolean) =>
      api<Report>(`/schedules/${id}/level`, { body: { limits: chosen, zones: fronts, mode, apply } }),
    onSuccess: (r) => {
      setRep(r);
      if (r.applied && r.view) {
        onView(r.view);
        toast(`Выровнено: сдвинуто работ ${r.moved.length}. Отменить можно в журнале правок`, 'success');
        onClose();
      }
    },
    onError: (e) => toast(e.message, 'danger'),
  });
  const canApply = view.can.edit && view.schedule.status !== 'approved';
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      width="lg"
      title="Выравнивание ресурсов"
      description="Укажите, сколько людей и машин доступно одновременно. Пусто — ресурс не выравнивается."
      footer={
        <>
          <Button onClick={onClose} className="max-md:h-11">
            Закрыть
          </Button>
          <Button
            disabled={!chosen.length && !fronts.length}
            loading={run.isPending && !run.variables}
            onClick={() => run.mutate(false)}
            className="max-md:h-11"
          >
            Посчитать
          </Button>
          {canApply ? (
            <Button
              variant="primary"
              disabled={!rep?.moved.length}
              loading={run.isPending && run.variables === true}
              onClick={() => run.mutate(true)}
              className="max-md:h-11"
            >
              Применить
            </Button>
          ) : null}
        </>
      }
    >
      {q.isPending ? <Spinner /> : null}
      {q.isError ? <ErrorBox error={q.error} /> : null}
      {q.data ? (
        <div className="flex flex-col gap-4">
          {!q.data.length ? (
            <p className="text-[14px] text-ink-2">
              На работах графика нет людей и техники — назначьте их в карточках работ.
            </p>
          ) : (
            <ul className="divide-y divide-line text-[14px]">
              {q.data.map((r) => (
                <li key={r.resourceId} className="flex flex-wrap items-center justify-between gap-2 py-1.5">
                  <span className="min-w-0">
                    {r.name}
                    <span className="block text-[12px] text-ink-2">
                      пик по графику {r.peak}
                      {r.onSite != null ? `, на проекте ${r.onSite}` : ''}
                    </span>
                  </span>
                  <Input
                    inputMode="decimal"
                    value={limits[r.resourceId] ?? ''}
                    onChange={(e) => edit(setLimits)({ ...limits, [r.resourceId]: e.target.value })}
                    aria-label={`Доступно: ${r.name}`}
                    placeholder="доступно"
                    className="w-28 max-md:h-11"
                  />
                </li>
              ))}
            </ul>
          )}
          {view.zones.length ? (
            <div className="text-[14px]">
              <p className="mb-1 font-medium">Фронт работ: сколько работ одновременно на захватке</p>
              <ul className="divide-y divide-line">
                {view.zones.map((z) => (
                  <li key={z.id} className="flex flex-wrap items-center justify-between gap-2 py-1.5">
                    <span className="min-w-0">{z.name}</span>
                    <Input
                      inputMode="numeric"
                      value={zones[z.id] ?? ''}
                      onChange={(e) => edit(setZones)({ ...zones, [z.id]: e.target.value })}
                      aria-label={`Работ одновременно: ${z.name}`}
                      placeholder="без предела"
                      className="w-28 max-md:h-11"
                    />
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          <fieldset className="flex flex-col gap-1 border-0 p-0 text-[14px]">
            <label className="flex min-h-11 items-center gap-2 md:min-h-0">
              <input type="radio" checked={mode === 'float'} onChange={() => edit(setMode)('float')} />В
              пределах резерва — окончание проекта не сдвигается
            </label>
            <label className="flex min-h-11 items-center gap-2 md:min-h-0">
              <input type="radio" checked={mode === 'extend'} onChange={() => edit(setMode)('extend')} />
              Со сдвигом окончания — работы сдвигаются, сколько нужно
            </label>
          </fieldset>
          {rep ? (
            <div className="flex flex-col gap-2 rounded-md bg-surface-2 px-3 py-2.5 text-[13px]">
              <p
                className={clsx(
                  rep.finishAfter &&
                    rep.finishBefore &&
                    rep.finishAfter > rep.finishBefore &&
                    'font-semibold text-danger',
                )}
              >
                Окончание проекта: {fmtMoment(rep.finishBefore)} → {fmtMoment(rep.finishAfter)}. Сдвинуто
                работ: {rep.moved.length}.
              </p>
              <ul className="ml-4 list-disc">
                {rep.peaks.map((p) => (
                  <li key={p.resourceId} className={clsx(p.after > p.limit && 'text-danger')}>
                    {p.name}: пик {p.before} → {p.after}, доступно {p.limit}
                  </li>
                ))}
              </ul>
              {rep.unresolved.length ? (
                <p className="text-danger">
                  Не снято перегрузок: {rep.unresolved.length} (первая — {rep.unresolved[0]!.resource},{' '}
                  {dd(rep.unresolved[0]!.day)}: нужно {rep.unresolved[0]!.need}, доступно{' '}
                  {rep.unresolved[0]!.limit}).{' '}
                  {mode === 'float' ? 'Резерва не хватает — попробуйте режим со сдвигом окончания.' : ''}
                </p>
              ) : null}
              {rep.stoppedEarly ? (
                <p className="text-ink-2">Расчёт остановлен по времени — результат частичный.</p>
              ) : null}
              {rep.moved.length ? (
                <details>
                  <summary className="min-h-11 cursor-pointer md:min-h-0">Сдвинутые работы</summary>
                  <ul className="ml-4 list-disc">
                    {rep.moved.slice(0, 100).map((m) => (
                      <li key={m.id}>
                        {m.code} {m.name}: {fmtMoment(m.startBefore)} → {fmtMoment(m.startAfter)}
                      </li>
                    ))}
                  </ul>
                </details>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </Dialog>
  );
}
